/**
 * PATH: packages/ui/src/components/email/row/resolveRowAccent.ts
 * FILE_ID: UI-EMAIL-ROW-RESOLVEACCENT-001
 * PURPOSE: Pure email→accent resolver. Given the allocation frame, the
 *          category visuals and the host's review handlers, it derives the
 *          row's workflow accent (colour + label + icon) and the single
 *          review (✓/✗) config the action cluster renders.
 * WHY:     The pre-refactor EmailRow inlined ~80 lines of branching to work
 *          out the frame kind, colour, label, which confirm/reject handler
 *          applies and whether the review is pending. Lifting it to a pure
 *          f(input)→output keeps the composer thin and makes the workflow
 *          logic unit-testable in isolation (rule 1.6).
 * FLOW:
 *   ┌──────────────────────────┐   ┌───────────────────┐   ┌───────────┐
 *   │ frame · category · email │ → │ resolveRowAccent  │ → │ RowAccent │
 *   └──────────────────────────┘   └───────────────────┘   └───────────┘
 * RELATED:
 *   - row/rowGrammar.ts: consumes ``accentColor`` for the left rail.
 *   - row/RowWorkChip.tsx: consumes the frame label/icon/href.
 *   - row/RowActions.tsx: consumes the ``review`` config.
 */
import {
  Briefcase,
  LayoutGrid,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'

import type { EmailMessage } from '../types'
import { shortenEntityLabel } from '../entityLabel'
import type { AllocationFrame, EmailRowCategoryConfig } from './rowTypes'

/** The single review (accept/reject) config the row exposes. At most one
 *  pair is ever active (job OR board OR suggestion), so the row never shows
 *  duplicated tick/cross. */
export interface RowReview {
  showConfirm: boolean
  showReject: boolean
  confirmLabel: string
  rejectLabel: string
  busy: boolean
  onConfirm?: () => void | Promise<void>
  onReject?: () => void | Promise<void>
}

export interface RowAccent {
  /** This row belongs to a workflow (board / linked job / AI suggestion). */
  isAllocated: boolean
  /** Concrete CSS colour for the left rail + frame tag. Category hue wins
   *  (operator: "proper colour matching the category"), then the allocation
   *  colour, then a neutral indigo. */
  accentColor: string
  frameKind: 'board' | 'job' | 'suggested'
  frameIsBoard: boolean
  frameIsJob: boolean
  isSuggested: boolean
  FrameIcon: LucideIcon
  /** Display-shortened tag label (e.g. "Watpac Construction"). */
  frameLabel: string
  /** Full label for the hover title (e.g. "#5735 — Watpac Construction"). */
  frameLabelFull: string
  /** Host-resolved deep link for a board/suggested frame tag. */
  href?: string
  review: RowReview
}

export interface ResolveRowAccentInput {
  allocationFrame?: AllocationFrame | null
  category: EmailRowCategoryConfig
  email: Pick<EmailMessage, 'job_id' | 'job_name' | 'job_auto_status'>
  onConfirmJob?: () => void | Promise<void>
  onRejectJob?: () => void | Promise<void>
  isConfirmingJob?: boolean
  isRejectingJob?: boolean
  onConfirmAllocation?: () => void | Promise<void>
  onRejectAllocation?: () => void | Promise<void>
  isAllocationBusy?: boolean
  onConfirmSuggestion?: () => void | Promise<void>
  onRejectSuggestion?: () => void | Promise<void>
  isSuggestionBusy?: boolean
}

/**
 * resolveRowAccent(input) → RowAccent. Pure: no side effects, no host hooks.
 */
export function resolveRowAccent({
  allocationFrame,
  category,
  email,
  onConfirmJob,
  onRejectJob,
  isConfirmingJob = false,
  isRejectingJob = false,
  onConfirmAllocation,
  onRejectAllocation,
  isAllocationBusy = false,
  onConfirmSuggestion,
  onRejectSuggestion,
  isSuggestionBusy = false,
}: ResolveRowAccentInput): RowAccent {
  const isAllocated = !!allocationFrame
  const accentColor = category.color || allocationFrame?.color || '#6366f1'
  const frameKind = allocationFrame?.kind ?? 'job'
  const frameIsBoard = isAllocated && frameKind === 'board'
  const isSuggested = isAllocated && frameKind === 'suggested'
  const frameIsJob = isAllocated && !frameIsBoard
  const FrameIcon = isSuggested ? Sparkles : frameIsBoard ? LayoutGrid : Briefcase

  const frameLabelFull =
    allocationFrame?.label ||
    (frameIsBoard ? 'Allocated' : email.job_name || `Job #${email.job_id}`)
  const frameLabel = shortenEntityLabel(frameLabelFull)

  const onConfirm = isSuggested
    ? onConfirmSuggestion
    : frameIsBoard
      ? onConfirmAllocation
      : onConfirmJob
  const onReject = isSuggested
    ? onRejectSuggestion
    : frameIsBoard
      ? onRejectAllocation
      : onRejectJob
  const busy = isSuggested
    ? isSuggestionBusy
    : frameIsBoard
      ? isAllocationBusy
      : isConfirmingJob || isRejectingJob
  // "Awaiting your tick/cross": a suggestion always is; a board frame per the
  // AI_SUGGESTED_PENDING flag; a job frame per the AI job-link status.
  const pending = isSuggested
    ? true
    : frameIsBoard
      ? !!allocationFrame?.pending
      : email.job_auto_status === 'pending'
  const confirmLabel = isSuggested
    ? 'Accept suggested job'
    : frameIsBoard
      ? 'Confirm this AI board allocation'
      : 'Confirm this AI job allocation'
  const rejectLabel = isSuggested
    ? 'Dismiss suggested job'
    : frameIsBoard
      ? 'Reject this board allocation'
      : 'Reject this job allocation'
  const showConfirm = isAllocated && pending && !!onConfirm
  // Linked-job frames keep the ✗ available whenever a reject handler exists
  // (you can reject a confirmed/human link too); board + suggestion frames
  // only expose it while still pending.
  const showReject =
    isAllocated && !!onReject && ((frameIsJob && !isSuggested) || pending)

  return {
    isAllocated,
    accentColor,
    frameKind,
    frameIsBoard,
    frameIsJob,
    isSuggested,
    FrameIcon,
    frameLabel,
    frameLabelFull,
    href: allocationFrame?.href,
    review: {
      showConfirm,
      showReject,
      confirmLabel,
      rejectLabel,
      busy,
      onConfirm,
      onReject,
    },
  }
}
